import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from "react-router-dom";
import {
	FLIGHT_BEST_SORT,
	FLIGHT_CHEAPEST_SORT,
	FLIGHT_DEPOSIT_SORT,
	FLIGHT_WEEKLY_SORT,
} from "../../constants";
import { FlightClassOption } from "../../constants/Options";
import { LocaleContextProvider, useLocale } from "../../hooks/locale";
import { SiteContextProvider, useSite } from "../../hooks/site";
import { loadUrlParamsSearch } from "../../redux/modules/search";
import { updateSiteLocaleId } from "../../utils/SiteUtils";
import {
	BookingDetail,
	Bookings,
	ConfirmBooking,
	Login,
	PassengerDetail,
	RedesignFlightSearchPage,
	Refer,
	ReviewPage,
} from "../LoadingView";
import BookingManagement from "../bookingdetail/redesign/BookingManagement";
import FinaliseAccount from "../redesign/finaliseaccount/FinaliseAccount";
import SearchControls from "../redesign/homepage/SearchControls";
import Navbar from "../redesign/navbar/Navbar";
import AppLayout from "./AppLayout";
import { HomeRedirect, LegacyToWebflowRedirect, Redirect } from "./Redirect";
import { localeByLocaleId } from "./localeByLocaleId";

export const sites = {
	[process.env.REACT_APP_SITE_HOSTNAME]: {
		backendApiUrl: process.env.REACT_APP_SITE_API_URL,
		bookingManagementApiUrl:
			process.env.REACT_APP_SITE_BOOKING_MANAGEMENT_API_URL,
		defaultLocaleId: "en-us",
		supportedLocaleIds: `${process.env.REACT_APP_SITE_SUPPORTED_LOCALE_IDS}`
			.split(",")
			.filter((localeId) => localeId.trim().length > 0),
	},
};

const auDomain = process.env.REACT_APP_SITE_AU_HOSTNAME;
if (auDomain) {
	sites[auDomain] = {
		backendApiUrl: process.env.REACT_APP_SITE_AU_API_URL,
		bookingManagementApiUrl:
			process.env.REACT_APP_SITE_AU_BOOKING_MANAGEMENT_API_URL,
		defaultLocaleId: "en-au",
		supportedLocaleIds: [],
	};
}

const caDomain = process.env.REACT_APP_SITE_CA_HOSTNAME;
if (caDomain) {
	sites[caDomain] = {
		backendApiUrl: process.env.REACT_APP_SITE_CA_API_URL,
		bookingManagementApiUrl:
			process.env.REACT_APP_SITE_CA_BOOKING_MANAGEMENT_API_URL,
		defaultLocaleId: "en-ca",
		supportedLocaleIds: [],
	};
}

const SiteWrapper = ({ children }) => {
	const { hostname } = window.location;
	return (
		<SiteContextProvider value={sites[hostname]}>
			{children}
		</SiteContextProvider>
	);
};

const webflowActive = process.env.REACT_APP_WEBFLOW_ACTIVE === "true";

const getInt = (s) => {
	const v = Number.parseInt(s);
	if (!Number.isNaN(v)) {
		return v;
	}
};

const filterUndefinedValues = (obj) =>
	Object.entries(obj)
		.filter(([k, v]) => v !== undefined)
		.reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

const VALID_CABIN_CLASS_CODES = FlightClassOption.filter(
	(item) => item.toShow,
).map((item) => item.value);

const DEFAULT_CABIN_CLASS_CODE = "Y";

function validateCabinClass(cabinClass) {
	if (VALID_CABIN_CLASS_CODES.includes(cabinClass)) {
		return cabinClass;
	}

	return DEFAULT_CABIN_CLASS_CODE;
}

function urlParamToValue(urlParam, transforms = {}) {
	return transforms[urlParam];
}

function urlParamToArray(urlParam, itemLength = 1, transforms = {}) {
	return urlParam && urlParam.length > 0 && urlParam.length % itemLength === 0
		? urlParam
				.match(new RegExp(".".repeat(itemLength), "g"))
				.map((x) => transforms[x] ?? x)
		: [];
}

const AppRoute = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const auth = useSelector((state) => state.auth);
	const [viewModel, setViewModel] = useState({
		status: "VIEW_NOT_LOADED",
		payload: {},
	});
	useEffect(() => {
		const location = window.location;
		const searchParams = new URLSearchParams(location.search);

		const params = filterUndefinedValues({
			adults: getInt(searchParams.get("adults")),
			children: getInt(searchParams.get("children")),
			infants: getInt(searchParams.get("infants")),
			cabinClass: validateCabinClass(searchParams.get("cabinClass")),
			stops: urlParamToArray(searchParams.get("stops")),
			carriers: urlParamToArray(searchParams.get("carriers"), 2, {
				XX: "COMBINATIONS",
			}),
			depart: urlParamToArray(searchParams.get("depart")),
			arrive: urlParamToArray(searchParams.get("arrive")),
			sort: urlParamToValue(searchParams.get("sort"), {
				B: FLIGHT_BEST_SORT,
				C: FLIGHT_CHEAPEST_SORT,
				D: FLIGHT_DEPOSIT_SORT,
				W: FLIGHT_WEEKLY_SORT,
			}),
			url: `${location.pathname}${location.search}`,
		});

		dispatch(loadUrlParamsSearch(params));
	}, []);

	return (
		<SiteWrapper>
			<Routes>
				<Route path="/flightssearch" element={<EmbeddedSearchControls />} />
				<Route path="/" element={<AppLayout useLocaleId={false} />}>
					<Route
						path="/"
						index
						element={
							webflowActive ? <HomeRedirect /> : <LocalEmbeddedSearchControls />
						}
					/>
					<Route
						path="flightssearch/s/:multicity"
						element={<RedesignFlightSearchPage />}
					/>
					<Route
						path="flightssearch/s/:origin/:destination/:departureDate/:returnDate"
						element={<RedesignFlightSearchPage />}
					/>
					<Route
						path="flightssearch/s/:origin/:destination/:departureDate"
						element={<RedesignFlightSearchPage />}
					/>
					<Route path="flightssearch/passenger" element={<PassengerDetail />} />
					<Route path="flights/review" element={<ReviewPage />} />
					<Route path="confirm" element={<ConfirmBooking />} />
					<Route path="finaliseaccount" element={<FinaliseAccount />} />
					<Route path="confirmbooking" element={<ConfirmBooking />} />
					<Route
						path="login"
						element={
							<RestrictedRoute auth={auth}>
								<Login />
							</RestrictedRoute>
						}
					/>
					<Route
						path="refer"
						element={
							<PrivateRoute auth={auth}>
								<Refer />
							</PrivateRoute>
						}
					/>
					<Route
						path="bookings"
						element={
							<PrivateRoute auth={auth}>
								<Bookings />
							</PrivateRoute>
						}
					/>
					<Route
						path="view/:path/*"
						element={
							<PrivateRoute auth={auth}>
								<BookingManagement />
							</PrivateRoute>
						}
					/>
					<Route
						path="bookings/:reference"
						element={
							<PrivateRoute auth={auth}>
								<BookingDetail />
							</PrivateRoute>
						}
					/>
					{/* legacy urls for if someone land on it from the AI or Google */}
					<Route
						path="aboutus"
						element={<LegacyToWebflowRedirect to="about-us" />}
					/>
					<Route
						path="how-it-works"
						element={<LegacyToWebflowRedirect to="how-it-works" />}
					/>
					<Route
						path="testimonial"
						element={<LegacyToWebflowRedirect to="testimonials" />}
					/>
					<Route
						path="privacy-policy"
						element={<LegacyToWebflowRedirect to="privacy-policy" />}
					/>
					<Route
						path="terms"
						element={<LegacyToWebflowRedirect to="terms-conditions" />}
					/>
				</Route>
				<Route
					path="/:localeId/flightssearch"
					element={<EmbeddedSearchControls />}
				/>
				<Route path="/:localeId" element={<AppLayout />}>
					<Route
						index
						element={
							webflowActive ? <HomeRedirect /> : <LocalEmbeddedSearchControls />
						}
					/>
					<Route
						path="flightssearch/s/:multicity"
						element={<RedesignFlightSearchPage />}
					/>
					<Route
						path="flightssearch/s/:origin/:destination/:departureDate/:returnDate"
						element={<RedesignFlightSearchPage />}
					/>
					<Route
						path="flightssearch/s/:origin/:destination/:departureDate"
						element={<RedesignFlightSearchPage />}
					/>
					<Route path="flightssearch/passenger" element={<PassengerDetail />} />
					<Route path="flights/review" element={<ReviewPage />} />
					<Route path="confirm" element={<ConfirmBooking />} />
					<Route path="finaliseaccount" element={<FinaliseAccount />} />
					<Route path="confirmbooking" element={<ConfirmBooking />} />
					<Route
						path="login"
						element={
							<RestrictedRoute auth={auth}>
								<Login />
							</RestrictedRoute>
						}
					/>
					<Route
						path="refer"
						element={
							<PrivateRoute auth={auth}>
								<Refer />
							</PrivateRoute>
						}
					/>
					<Route
						path="bookings"
						element={
							<PrivateRoute auth={auth}>
								<Bookings />
							</PrivateRoute>
						}
					/>
					<Route
						path="view/:path/*"
						element={
							<PrivateRoute auth={auth}>
								<BookingManagement />
							</PrivateRoute>
						}
					/>
					<Route
						path="bookings/:reference"
						element={
							<PrivateRoute auth={auth}>
								<BookingDetail />
							</PrivateRoute>
						}
					/>
				</Route>
				<Route path="/*" element={<MissingRoute />} />
			</Routes>
		</SiteWrapper>
	);
};

const alreadyLocaleIdPrefixed = /^\/(en-(au|us|ca|gb))/;

const MissingRoute = ({ localeId }) => {
	const { defaultLocaleId } = useSite();
	const { webflowHomepageUrl } = localeByLocaleId[defaultLocaleId];

	return <Redirect to={webflowHomepageUrl} />;
};

const RestrictedRoute = ({ auth, children }) => {
	const { locale } = useLocale();

	if (auth.isAuthenticated) {
		return <Navigate to={"/bookings"} replace />;
	}

	return children;
};

const PrivateRoute = ({ auth, children }) => {
	const { localeId } = useLocale();

	if (!auth.isAuthenticated) {
		// not logged in so redirect to login page with the return url
		return <Navigate to={"/login"} replace />;
	}

	return children;
};

const EmbeddedSearchControls = () => {
	const { supportedLocaleIds, defaultLocaleId } = useSite();
	const { localeId } = useParams();

	useEffect(() => {
		updateSiteLocaleId();
	}, []);

	return (
		<LocaleContextProvider
			value={
				localeByLocaleId[
					localeId && supportedLocaleIds.includes(localeId)
						? localeId
						: defaultLocaleId
				]
			}
		>
			<SearchControls />
		</LocaleContextProvider>
	);
};

const LocalEmbeddedSearchControls = () => {
	const { auth } = useSelector((state) => state);

	return (
		<Navbar auth={auth}>
			<EmbeddedSearchControls />
		</Navbar>
	);
};

export default AppRoute;
